import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CONNECTION_TEMPLATES,
  type CapabilityRow,
  type ConnectionDTO,
  type ProjectDetailDTO,
} from "@app-factory/shared";
import { api } from "@/lib/api";
import { DataTable } from "@/design/DataTable";
import { cs } from "@/i18n/cs";
import { Btn, EmptyState, Field, Panel, inputClass } from "@/design/primitives";
import { canManage, useProjectRole } from "../project-data";

const GROUPS: { key: string; label: string }[] = [
  { key: "DEVELOPMENT", label: "DEVELOPMENT" },
  { key: "ANALYTICS", label: "ANALYTICS" },
  { key: "BUSINESS", label: "BUSINESS" },
  { key: "OTHER", label: "OSTATNÍ" },
];

const MODE_COLOR: Record<string, { color: string; border: string }> = {
  AUTO: { color: "var(--positive)", border: "color-mix(in srgb, var(--positive) 40%, transparent)" },
  APPROVAL: { color: "var(--warning)", border: "color-mix(in srgb, var(--warning) 45%, transparent)" },
  DENIED: { color: "var(--text-tertiary)", border: "var(--border-default)" },
};

const STATUS_META: Record<ConnectionDTO["status"], { label: () => string; color: string; border: string; bg: string }> = {
  CONNECTED: { label: () => cs.connections.connected, color: "var(--positive)", border: "color-mix(in srgb, var(--accent) 18%, transparent)", bg: "transparent" },
  MISSING: { label: () => cs.connections.missing, color: "var(--text-tertiary)", border: "var(--border-default)", bg: "var(--border-default)" },
  ERROR: { label: () => cs.connections.error, color: "var(--critical)", border: "color-mix(in srgb, var(--critical) 40%, transparent)", bg: "color-mix(in srgb, var(--critical) 3%, transparent)" },
};

/** CONNECTIONS TAB dle /Design: Integration Map + capability matrix (spec §56–58). */
export function ConnectionsTab({ project }: { project: ProjectDetailDTO }) {
  const role = useProjectRole(project);
  const manager = canManage(role);
  const query = useQuery({
    queryKey: ["section", project.id, "connections"],
    queryFn: () => api.connections(project.id),
  });
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const list = query.data?.connections ?? [];
  const open = list.find((c) => c.id === openId) ?? null;

  return (
    <div className="mt-4">
      <div className="mb-3.5 flex flex-wrap items-center justify-between gap-2">
        <span className="af-label-dim">{cs.connections.header}</span>
        {manager ? (
          <Btn onClick={() => setAdding(true)} data-testid="connection-add">
            {cs.connections.connect}
          </Btn>
        ) : null}
      </div>

      {adding ? <AddConnectionForm project={project} existing={list} onClose={() => setAdding(false)} /> : null}

      {list.length === 0 && !adding ? (
        <Panel className="p-8">
          <EmptyState title={cs.connections.empty} />
        </Panel>
      ) : (
        <div className="grid grid-cols-1 items-start gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
          {GROUPS.filter((g) => list.some((c) => c.group === g.key)).map((g) => (
            <Panel key={g.key} className="p-4" data-testid={`conn-group-${g.key}`}>
              <div className="af-label mb-3" style={{ color: "var(--accent)" }}>
                {g.label}
              </div>
              <div className="flex flex-col gap-2">
                {list
                  .filter((c) => c.group === g.key)
                  .map((c) => {
                    const meta = STATUS_META[c.status];
                    return (
                      <button
                        key={c.id}
                        type="button"
                        className="cursor-pointer px-3 py-2.5 text-left"
                        style={{ borderRadius: "var(--radius-box)", border: `1px solid ${meta.border}`, background: meta.bg }}
                        onClick={() => setOpenId(c.id)}
                        data-testid={`connection-${c.key}`}
                      >
                        <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-2">
                          <span className="min-w-0 text-[12px]" style={{ color: "var(--text-primary)" }}>
                            {c.icon} {c.name}
                          </span>
                          <span className="text-[11px]" style={{ color: meta.color }}>
                            {meta.label()} {cs.connections.matrix}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {c.capabilities.slice(0, 4).map((cap) => {
                            const mode =
                              (c.status === "MISSING" ? MODE_COLOR.DENIED : MODE_COLOR[cap.mode]) ??
                              { color: "var(--text-tertiary)", border: "var(--border-default)" };
                            return (
                              <span
                                key={cap.action}
                                className="px-1.5 py-0.5 text-[11px]"
                                style={{ borderRadius: "var(--radius-box)", color: mode.color, border: `1px solid ${mode.border}` }}
                              >
                                {cap.action}
                              </span>
                            );
                          })}
                          {c.capabilities.length > 4 ? (
                            <span className="px-1 py-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                              +{c.capabilities.length - 4}
                            </span>
                          ) : null}
                        </div>
                      </button>
                    );
                  })}
              </div>
            </Panel>
          ))}
        </div>
      )}

      <div className="mt-3.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        {cs.connections.legend}{" "}
        <span style={{ color: "var(--positive)" }}>{cs.connections.legendAuto}</span> ·{" "}
        <span style={{ color: "var(--warning)" }}>{cs.connections.legendApproval}</span> ·{" "}
        <span style={{ color: "var(--text-tertiary)" }}>{cs.connections.legendDenied}</span>
      </div>

      {open ? (
        <CapabilityMatrixModal
          connection={open}
          projectId={project.id}
          manager={manager}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </div>
  );
}

/** Capability matrix modal (spec §57) + secrets správa (spec §58). */
function CapabilityMatrixModal({
  connection,
  projectId,
  manager,
  onClose,
}: {
  connection: ConnectionDTO;
  projectId: string;
  manager: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [secretName, setSecretName] = useState("");
  const [secretValue, setSecretValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["section", projectId, "connections"] });

  const modeMutation = useMutation({
    mutationFn: (capabilities: CapabilityRow[]) =>
      api.updateConnection(connection.id, { capabilities }),
    onSuccess: invalidate,
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });
  const secretMutation = useMutation({
    mutationFn: () => api.setConnectionSecret(connection.id, { name: secretName.trim(), value: secretValue }),
    onSuccess: () => {
      setSecretName("");
      setSecretValue("");
      setError(null);
      void invalidate();
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });
  const removeSecret = useMutation({
    mutationFn: (name: string) => api.deleteConnectionSecret(connection.id, name),
    onSuccess: invalidate,
  });
  const disconnect = useMutation({
    mutationFn: () => api.deleteConnection(connection.id),
    onSuccess: () => {
      void invalidate();
      onClose();
    },
  });

  const cycleMode = (row: CapabilityRow) => {
    if (!manager) return;
    const next = row.mode === "AUTO" ? "APPROVAL" : row.mode === "APPROVAL" ? "DENIED" : "AUTO";
    modeMutation.mutate(
      connection.capabilities.map((c) => (c.action === row.action ? { ...c, mode: next } : c)),
    );
  };

  const meta = STATUS_META[connection.status];

  // portál do <body>: obsah stránky je v `af-animate-fade`, což je vlastní
  // vrstva — modál uvnitř by se nedostal nad rail ani nad sticky prvky
  return createPortal(
<div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
      style={{ background: "var(--surface-overlay)", backdropFilter: "blur(3px)" }}
      onClick={onClose}
      data-testid="capability-matrix"
    >
      <Panel brackets className="max-h-[85vh] w-full max-w-[680px] overflow-auto p-4 sm:p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-1 flex items-center justify-between gap-3">
          <span className="af-label min-w-0" style={{ color: "var(--accent)" }}>
            {cs.connections.matrixTitle(connection.name)}
          </span>
          <span className="flex flex-none items-center gap-3">
            <span className="text-[11px]" style={{ color: meta.color }}>
              {meta.label()}
            </span>
            <button type="button" className="cursor-pointer text-[11px]" style={{ color: "var(--text-secondary)" }} onClick={onClose}>
              ✕
            </button>
          </span>
        </div>
        <div className="mb-3.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {cs.connections.matrixNote}
        </div>

        <DataTable
          columns={[
            {
              key: "action",
              label: cs.connections.action,
              width: "minmax(160px, 1.4fr)",
              primary: true,
              cell: (row) => row.action,
            },
            {
              key: "mode",
              label: cs.connections.mode,
              width: "110px",
              cell: (row) => {
                const mode =
                  MODE_COLOR[row.mode] ?? {
                    color: "var(--text-tertiary)",
                    border: "var(--border-default)",
                  };
                return (
                  <button
                    type="button"
                    className="af-mono px-2.5 py-1 text-[10px]"
                    style={{
                      borderRadius: "var(--radius-pill)",
                      color: mode.color,
                      border: `1px solid ${mode.border}`,
                      cursor: manager ? "pointer" : "default",
                    }}
                    title={manager ? "kliknutím přepneš AUTO → APPROVAL → DENIED" : undefined}
                    onClick={() => cycleMode(row)}
                    data-testid={`cap-mode-${row.action}`}
                  >
                    {row.mode}
                  </button>
                );
              },
            },
            {
              key: "note",
              label: cs.connections.note,
              width: "minmax(160px, 1.4fr)",
              cell: (row) => <span style={{ color: "var(--text-tertiary)" }}>{row.note}</span>,
            },
          ]}
          rows={connection.capabilities}
          rowKey={(row) => row.action}
        />

        <div className="mt-3 flex flex-wrap justify-between gap-x-3 gap-y-0.5 pt-2 text-[11px]" style={{ borderTop: "1px solid var(--border-subtle)" }}>
          <span style={{ color: "var(--text-tertiary)" }}>{cs.connections.depts}</span>
          <span style={{ color: "var(--accent)" }}>{connection.departmentsNote || "—"}</span>
        </div>

        {/* secrets (spec §58) */}
        <div className="mt-4 pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
          <div className="af-label" style={{ color: "var(--warning)" }}>
            {cs.connections.secrets}
          </div>
          <div className="mb-2 mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.connections.secretsNote}
          </div>
          {connection.secrets.map((sec) => (
            <div key={sec.name} className="flex items-center justify-between gap-2 py-1 text-[11px]" data-testid={`secret-${sec.name}`}>
              <span className="min-w-0 break-all font-mono" style={{ color: "var(--text-primary)" }}>
                {sec.name} <span style={{ color: "var(--text-tertiary)" }}>{sec.hint}</span>
              </span>
              {manager ? (
                <button
                  type="button"
                  className="flex-none cursor-pointer text-[11px]"
                  style={{ color: "var(--critical)" }}
                  onClick={() => removeSecret.mutate(sec.name)}
                >
                  ✕
                </button>
              ) : null}
            </div>
          ))}
          {manager ? (
            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-end">
              <input
                className={inputClass}
                placeholder={cs.connections.secretName}
                value={secretName}
                onChange={(e) => setSecretName(e.target.value)}
                data-testid="secret-name"
              />
              <input
                className={inputClass}
                type="password"
                placeholder={cs.connections.secretValue}
                value={secretValue}
                onChange={(e) => setSecretValue(e.target.value)}
                data-testid="secret-value"
              />
              <Btn
                disabled={!secretName.trim() || !secretValue || secretMutation.isPending}
                onClick={() => secretMutation.mutate()}
                data-testid="secret-save"
              >
                {cs.connections.secretSet}
              </Btn>
            </div>
          ) : null}
        </div>

        {error ? (
          <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
            ⚠ {error}
          </div>
        ) : null}

        {manager ? (
          <div className="mt-3.5 flex justify-end">
            <button
              type="button"
              className="cursor-pointer text-[11px]"
              style={{ color: "var(--critical)" }}
              onClick={() => {
                if (confirm(`Odpojit ${connection.name}? Smažou se i secrets.`)) disconnect.mutate();
              }}
            >
              ✕ {cs.connections.disconnect}
            </button>
          </div>
        ) : null}
      </Panel>
    </div>,
    document.body,
  );
}

function AddConnectionForm({
  project,
  existing,
  onClose,
}: {
  project: ProjectDetailDTO;
  existing: ConnectionDTO[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const available = CONNECTION_TEMPLATES.filter((t) => !existing.some((c) => c.key === t.key));
  const [key, setKey] = useState(available[0]?.key ?? "");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => api.createConnection(project.id, { key, note: "" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["section", project.id, "connections"] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });

  return (
    <Panel brackets className="mb-3.5 max-w-xl p-4" data-testid="connection-form">
      <Field label={cs.connections.pickTemplate}>
        <select className={inputClass} style={{ background: "var(--surface-inset)" }} value={key} onChange={(e) => setKey(e.target.value)} data-testid="connection-template">
          {available.map((t) => (
            <option key={t.key} value={t.key}>
              {t.icon} {t.name} ({t.group})
            </option>
          ))}
        </select>
      </Field>
      {error ? (
        <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
          ⚠ {error}
        </div>
      ) : null}
      <div className="mt-3 flex justify-end gap-2">
        <Btn onClick={onClose}>{cs.common.cancel}</Btn>
        <Btn variant="primary" disabled={!key || mutation.isPending} onClick={() => mutation.mutate()} data-testid="connection-save">
          {cs.common.create}
        </Btn>
      </div>
    </Panel>
  );
}
